import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/ui/Modal/Modal';
import { Field } from '@/components/ui/Field/Field';
import { TextInput } from '@/components/ui/TextInput/TextInput';
import { Button } from '@/components/ui/Button/Button';
import { useSession } from '@/services/auth';
import { useInviteUser } from '@/services/users';
import { useToast } from '@/components/ui/Toast/ToastProvider';
import { nb } from '@/i18n/nb';
import type { Role } from '@/types/api';
import s from './admin.module.css';
import form from '@/components/auth/AccessForm.module.css';

const schema = z.object({
  email: z.string().min(1, nb.auth.form.emailRequired).email(nb.auth.form.emailInvalid),
  displayName: z.string().min(1, nb.auth.form.nameRequired).max(64, nb.auth.form.nameMax),
  role: z.enum(['registered_guest', 'owner', 'admin']),
});

type Values = z.infer<typeof schema>;

export function InviteUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user: actor } = useSession();
  const invite = useInviteUser();
  const toast = useToast();
  // Owners may only invite registered guests; admins may invite at any role.
  const roleOptions: Role[] =
    actor?.role === 'admin' ? ['registered_guest', 'owner', 'admin'] : ['registered_guest'];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { role: 'registered_guest' },
  });

  function close() {
    onClose();
    reset();
  }

  const onSubmit = handleSubmit((values) => {
    invite.mutate(values, {
      onSuccess: () => {
        toast({
          title: nb.admin.toast.invitedTitle,
          body: nb.admin.toast.invitedBody.replace('{navn}', values.displayName || values.email),
        });
        close();
      },
      onError: (error) =>
        toast({
          title: nb.admin.toast.errorTitle,
          body: error instanceof Error ? error.message : undefined,
          error: true,
        }),
    });
  });

  return (
    <Modal open={open} onClose={close} title={nb.admin.invite.title}>
      <form className={form.form} onSubmit={onSubmit} noValidate>
        <p className={form.lede}>{nb.admin.invite.lede}</p>
        <Field label={nb.admin.invite.emailLabel} error={errors.email?.message}>
          {(wiring) => (
            <TextInput
              type="email"
              inputMode="email"
              invalid={Boolean(errors.email)}
              {...wiring}
              {...register('email')}
            />
          )}
        </Field>
        <Field label={nb.admin.invite.nameLabel} error={errors.displayName?.message}>
          {(wiring) => (
            <TextInput invalid={Boolean(errors.displayName)} {...wiring} {...register('displayName')} />
          )}
        </Field>
        <Field label={nb.admin.invite.roleLabel}>
          {(wiring) => (
            <select className={s.select} {...wiring} {...register('role')}>
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {nb.roles[r]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className={form.actions}>
          <Button type="button" variant="secondary" onClick={close}>
            {nb.common.cancel}
          </Button>
          <Button type="submit" variant="primary" loading={invite.isPending}>
            {nb.admin.invite.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
